import { MinusIcon, PlusIcon, SparklesIcon } from "@heroicons/react/24/solid";
import { PiMS } from "../../utils/PimsUtils";
import { Disclosure, Switch } from "@headlessui/react";
import { defaultVetspireRecapOptions, VetspireRecapOptions } from "../integrations/IntegrationUtils";
import { capitalize } from "lodash";
import { useEffect, useState } from "react";

interface VetspireRecapProps {
    advancedOptions: VetspireRecapOptions | undefined;
    setAdvancedOptions: (options: VetspireRecapOptions | undefined) => void;
    externalPatientId: string;
    setExternalPatientId: (id: string) => void;
    setSource: (source: PiMS | undefined) => void;
}

const VetspireRecap = ({setExternalPatientId, setSource, externalPatientId, setAdvancedOptions, advancedOptions = defaultVetspireRecapOptions}: VetspireRecapProps) => {

    const [includeVetspireRecords, setIncludeVetspireRecords] = useState<boolean>(true);

    useEffect(() => {
        if(externalPatientId.trim() && includeVetspireRecords) {
            setSource(PiMS.Vetspire);
            setAdvancedOptions(defaultVetspireRecapOptions);
        } else {
            setSource(undefined)
            setAdvancedOptions(undefined)
        }
    }, [setAdvancedOptions, setSource, externalPatientId, includeVetspireRecords]);
    
    const handlePatientIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setExternalPatientId(event.target.value);
    };

    return (
        <div className={`rounded-md bg-gradient-to-r from-[#cce0ff] to-[#d9fff6] p-4 shadow-[0_6px_8px_rgba(0,0,0,0.2)] flex flex-col gap-y-2 w-full justify-center ${!includeVetspireRecords ? "opacity-50" : ""}`}>
            <div className="flex flex-row gap-x-2 items-center justify-between">
                <div className="text-md font-semibold text-gray-900 flex flex-row gap-x-2 items-center">
                    <SparklesIcon className="h-5 w-5 text-blue-500 inline-block mr-1" aria-hidden="true" />
                    Summarize Records from Vetspire
                    <span className="inline-flex items-center px-2 py-1 text-xs font-bold text-white bg-blue-800 rounded-md">
                        Beta
                    </span>
                </div>
                <Switch
                    checked={includeVetspireRecords}
                    onChange={setIncludeVetspireRecords}
                    className={`${
                        includeVetspireRecords ? 'bg-green-500' : 'bg-gray-500'
                    } relative inline-flex h-6 w-11 items-center rounded-full flex-shrink-0`}
                >
                    <span
                        className={`${
                        includeVetspireRecords ? 'translate-x-6' : 'translate-x-1'
                        } inline-block h-4 w-4 transform rounded-full bg-white transition`}
                    />
                </Switch>
            </div>
            <div className="text-sm text-gray-600">Provide the patient ID for the patient's information you want to summarize</div>
            <input
                type="text"
                className="mt-2 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="Patient ID"
                value={externalPatientId}
                onChange={(e) => handlePatientIdChange(e)}
            />
            <span className="text-xs text-gray-600"><span className="font-semibold">Note:</span> This will only work if the patient is already in Vetspire.</span>
            <Disclosure>
                {({ open }) => (
                    <div className="flex flex-col gap-y-2 mt-2">
                        <Disclosure.Button className="text-sm text-blue-500 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md font-semibold gap-x-2 flex items-center justify-center" aria-label={open ? "Collapse summary" : "Expand summary"}>
                            Choose what to include in the summary
                            {open ? <MinusIcon className={`h-5 w-5 stroke-[5]`} aria-hidden="true" /> : <PlusIcon className={`h-5 w-5 stroke-[5]`} aria-hidden="true" />}
                        </Disclosure.Button>
                        <Disclosure.Panel className="text-sm text-gray-600 bg-white/50 p-2 rounded-md">
                            Pick what information to include in the summary:
                            <div className="grid grid-cols-2 gap-2 mt-2 font-semibold">
                                {Object.entries(advancedOptions).map(([key, value]) => (
                                    <div key={key} className="flex items-center mt-2">
                                        <input
                                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            type="checkbox"
                                            id={key}
                                            checked={value as boolean}
                                            onChange={(e) => {
                                                    setAdvancedOptions({
                                                        ...advancedOptions,
                                                        [key]: e.target.checked,
                                                    });
                                                }
                                            }
                                        />
                                        <label htmlFor={key} className="ml-2">{capitalize(key.replace(/_/g, " "))}</label>
                                    </div>
                                ))}
                            </div>
                        </Disclosure.Panel>
                    </div>
                )}
            </Disclosure>
        </div>
    );
}

export default VetspireRecap;